import { perkpointApi } from "./client";

export type CreateInviteCodePayload = {
  code: string;
  type: "single_use" | "multi_use";
  tier_key: string;
  joining_perks: string | null;
  restricted_to: "anyone" | "email" | "phone";
  invitee_detail: string | null;
  tier_lock_until: string | null;
  valid_until: string;
  usage_cap: number;
};

export type UpdateInviteCodePayload = Partial<
  Omit<CreateInviteCodePayload, "code">
>;

export type PerkPointInviteCode = {
  id: string;
  program_id: string;
  code: string;
  type: string;
  code_type?: string;
  tier_id: string;
  tier_key: string;
  tier_name: string;
  joining_perks: string | { description?: string } | null;
  joining_perks_label?: string | null;
  restricted_to: string;
  invitee_detail?: string | null;
  tier_lock_until: string | null;
  valid_until: string | null;
  usage_cap: number;
  max_redemptions?: number;
  redemption_count?: number;
  used_label?: string;
  status: string;
  created_at?: string;
};

export const inviteCodeKeys = {
  all: ["perkpoint", "invite-codes"] as const,
  list: (programId: string) =>
    ["perkpoint", "invite-codes", programId, "list"] as const,
};

const base = (programId: string) =>
  `/admin/programs/${programId}/invite-codes`;

function unwrapInviteCodes(data: unknown): PerkPointInviteCode[] {
  if (Array.isArray(data)) return data as PerkPointInviteCode[];
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    for (const key of ["data", "items", "invite_codes", "codes"]) {
      if (Array.isArray(record[key])) {
        return record[key] as PerkPointInviteCode[];
      }
    }
  }
  return [];
}

export const InviteCodesApi = {
  async create(programId: string, payload: CreateInviteCodePayload) {
    const { data } = await perkpointApi.post(base(programId), payload);
    return data;
  },

  async update(
    programId: string,
    inviteCodeId: string,
    payload: UpdateInviteCodePayload,
  ) {
    const { data } = await perkpointApi.patch(
      `${base(programId)}/${inviteCodeId}`,
      payload,
    );
    return data;
  },

  async list(programId: string) {
    const { data } = await perkpointApi.get(base(programId));
    return unwrapInviteCodes(data);
  },
};
